import { useMemo } from 'react';
import { Check, X, Shield, ShieldAlert, ShieldCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Top 100 most common passwords (trimmed set for client-side check)
const COMMON_PASSWORDS = new Set([
  'password', '123456', '12345678', 'qwerty', 'abc123', 'monkey', '1234567',
  'letmein', 'trustno1', 'dragon', 'baseball', 'iloveyou', 'master', 'sunshine',
  'ashley', 'michael', 'shadow', '123123', '654321', 'superman', 'qazwsx',
  'football', 'password1', 'password123', 'welcome', 'welcome1', 'p@ssw0rd',
  'admin', 'login', 'starwars', 'solo', 'princess', 'passw0rd', 'hello',
  'charlie', 'donald', 'lovely', 'freedom', 'whatever', 'qwerty123',
  '000000', '111111', '121212', '123456789', '1234567890', 'access',
  'flower', 'hottie', 'jesus', 'loveme', 'ninja', 'test', 'soccer',
  'basketball', 'football1', 'sportara',
]);

interface PasswordCheck {
  label: string;
  met: boolean;
}

interface PasswordStrength {
  score: number; // 0-4
  label: string;
  color: string;
  checks: PasswordCheck[];
  isCommon: boolean;
}

export function usePasswordStrength(password: string): PasswordStrength {
  return useMemo(() => {
    const checks: PasswordCheck[] = [
      { label: '8+ characters', met: password.length >= 8 },
      { label: 'Uppercase letter', met: /[A-Z]/.test(password) },
      { label: 'Lowercase letter', met: /[a-z]/.test(password) },
      { label: 'Number', met: /\d/.test(password) },
      { label: 'Special character', met: /[^A-Za-z0-9]/.test(password) },
    ];

    const isCommon = COMMON_PASSWORDS.has(password.toLowerCase());
    const metCount = checks.filter(c => c.met).length;
    const score = isCommon ? Math.min(metCount, 1) : metCount;

    const labels = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'];
    const colors = [
      'hsl(var(--destructive))',
      'hsl(25, 95%, 53%)',
      'hsl(45, 93%, 47%)',
      'hsl(142, 71%, 45%)',
      'hsl(142, 76%, 36%)',
    ];

    return {
      score: Math.min(score, 4),
      label: labels[Math.min(score, 4)],
      color: colors[Math.min(score, 4)],
      checks,
      isCommon,
    };
  }, [password]);
}

interface PasswordStrengthMeterProps {
  password: string;
  show: boolean;
}

export function PasswordStrengthMeter({ password, show }: PasswordStrengthMeterProps) {
  const strength = usePasswordStrength(password);

  if (!show || !password) return null;

  const Icon = strength.score <= 1 ? ShieldAlert : strength.score <= 2 ? Shield : ShieldCheck;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        className="space-y-2"
      >
        {/* Strength bar */}
        <div className="flex items-center gap-2">
          <div className="flex-1 flex gap-1">
            {[0, 1, 2, 3, 4].map(i => (
              <div
                key={i}
                className="h-1.5 flex-1 rounded-full transition-all duration-300"
                style={{
                  backgroundColor: i <= strength.score - 1 ? strength.color : 'hsl(var(--muted))',
                }}
              />
            ))}
          </div>
          <div className="flex items-center gap-1">
            <Icon className="w-3.5 h-3.5" style={{ color: strength.color }} />
            <span className="text-[11px] font-display font-bold" style={{ color: strength.color }}>
              {strength.label}
            </span>
          </div>
        </div>

        {/* Common password warning */}
        {strength.isCommon && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-[11px] text-destructive font-body flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            This is a commonly breached password
          </motion.p>
        )}

        {/* Backend rejection warning */}
        {!strength.isCommon && strength.score >= 3 && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-[10px] text-muted-foreground/70 font-body flex items-center gap-1"
          >
            <Shield className="w-3 h-3" />
            Tip: Avoid dictionary words — the server may still reject common phrases
          </motion.p>
        )}

        {/* Requirements checklist */}
        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
          {strength.checks.map(check => (
            <div key={check.label} className="flex items-center gap-1">
              {check.met ? (
                <Check className="w-3 h-3 text-green-500" />
              ) : (
                <X className="w-3 h-3 text-muted-foreground/40" />
              )}
              <span className={`text-[10px] font-body ${check.met ? 'text-foreground' : 'text-muted-foreground/60'}`}>
                {check.label}
              </span>
            </div>
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
